'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { generateProductSlug } from '@shared/lib/slugify';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import { useSponsoredBanners } from '../providers/SponsoredBannersProvider';
import { Button } from '@/components';

type CarouselImage = {
  desktopUri: string;
  mobileUri?: string;
  order: number;
};

type SponsoredBanner = {
  id: string;
  title: string;
  bannerImageUri: string | null;
  desktopImageUri: string | null;
  mobileImageUri: string | null;
  bannerImages: CarouselImage[] | null;
  linkType: 'product' | 'collection' | 'brand' | 'custom';
  linkTarget: string | null;
  customUrl: string | null;
  position: string | null;
  order: number;
  covaLocationId?: number | null;
  hasCarousel?: boolean;
  desktopRatio?: string;
  mobileRatio?: string;
};

type SponsoredBannersProps = {
  position?: string; // Optional position filter
  limit?: number; // Optional limit on number of banners
  className?: string; // Optional custom className
  covaLocationId?: number | null; // Optional location ID for filtering
  banners?: SponsoredBanner[]; // Optional: pre-fetched banners (if provided, will filter by position instead of fetching)
};

export default function SponsoredBanners({ position, limit, className = '', covaLocationId, banners: providedBanners }: SponsoredBannersProps) {
  // Try to get banners from context first (if provider is used)
  const { banners: contextBanners, loading: contextLoading, provided: contextProvided } = useSponsoredBanners();
  const contextBannersData = contextBanners.length > 0 ? contextBanners : null;
  
  // Use provided banners, context banners (when loaded), or null
  const availableBanners = providedBanners || (contextProvided && !contextLoading ? contextBannersData : null);
  
  const [banners, setBanners] = useState<SponsoredBanner[]>([]);
  const [loading, setLoading] = useState(!availableBanners && !contextProvided); // If context is present but loading, stay loading
  const [isMobile, setIsMobile] = useState(false);
  const [carouselIndices, setCarouselIndices] = useState<Record<string, number>>({});
  const router = useRouter();
  const base = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

  // Detect mobile vs desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // If context is present but still loading, wait
    if (contextProvided && contextLoading) {
      setLoading(true);
      setBanners([]);
      return;
    }

    const applyFilters = (source: SponsoredBanner[]) => {
      let filtered = [...source];
      const normalizedStoreId =
        covaLocationId != null && Number.isFinite(Number(covaLocationId))
          ? Number(covaLocationId)
          : null;

      if (position) {
        filtered = filtered.filter((banner) => banner.position === position);
      }

      if (normalizedStoreId != null && normalizedStoreId > 0) {
        filtered = filtered
          .filter(
            (banner) =>
              banner.covaLocationId === normalizedStoreId ||
              banner.covaLocationId == null
          )
          .sort((a, b) => {
            const rank = (locationId?: number | null) =>
              locationId === normalizedStoreId ? 0 : locationId == null ? 1 : 2;
            const rankDiff = rank(a.covaLocationId) - rank(b.covaLocationId);
            if (rankDiff !== 0) return rankDiff;
            if (a.order !== b.order) return a.order - b.order;
            return a.id.localeCompare(b.id);
          });
      }

      if (limit && limit > 0) {
        filtered = filtered.slice(0, limit);
      }

      // Initialize carousel indices for banners with carousel
      const initialIndices: Record<string, number> = {};
      filtered.forEach(banner => {
        if (banner.bannerImages && banner.bannerImages.length > 0) {
          initialIndices[banner.id] = 0;
        }
      });

      setCarouselIndices(initialIndices);
      setBanners(filtered);
      setLoading(false);
    };

    // If banners are available (from props or context), filter them by index/position instead of fetching
    if (availableBanners) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SponsoredBanners] Using available banners (${availableBanners.length} total) for position: ${position || 'all'}`);
      }
      applyFilters(availableBanners);
      return;
    }

    // Otherwise, fetch banners from API (same filters as admin preview: position + optional store)
    const fetchBanners = async () => {
      setLoading(true);
      try {
        const sp = new URLSearchParams();
        if (position) {
          sp.set('position', position);
        }
        const loc =
          covaLocationId != null && Number.isFinite(Number(covaLocationId))
            ? Number(covaLocationId)
            : null;
        if (loc != null && loc > 0) {
          sp.set('covaLocationId', String(loc));
        }
        const qs = sp.toString();
        const url = `${base}/api/sponsored-banners/active${qs ? `?${qs}` : ''}`;

        const res = await fetch(url, {
          cache: 'no-store',
          credentials: 'include', // Include cookies for CORS if needed
        });
        
        if (!res.ok) {
          throw new Error(`Failed to fetch sponsored banners: ${res.status}`);
        }
        
        const data = await res.json();
        if (data?.success) {
          applyFilters(data.banners || []);
        } else {
          applyFilters(data?.banners || []);
        }
      } catch (error: unknown) {
        // Silently handle errors - backend may not be available during SSR
        if (process.env.NODE_ENV === 'development') {
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.warn('Failed to fetch sponsored banners from backend (backend may not be running):', errorMessage);
        }
        setBanners([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [base, position, limit, covaLocationId, availableBanners]);

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (banners.length === 0) {
    return null; // Don't render if no banners
  }

  const handleBannerClick = (banner: SponsoredBanner, e?: React.MouseEvent) => {
    // Don't navigate if clicking on carousel controls
    if (e && (e.target as HTMLElement).closest('.carousel-control')) {
      return;
    }
    
    if (banner.linkType === 'custom' && banner.customUrl) {
      // External URL - open in new tab if it's a full URL
      if (banner.customUrl.startsWith('http://') || banner.customUrl.startsWith('https://')) {
        window.open(banner.customUrl, '_blank', 'noopener,noreferrer');
      } else {
        // Internal relative URL
        router.push(banner.customUrl);
      }
    } else if (banner.linkType === 'product' && banner.linkTarget) {
      // Navigate to product page
      router.push(`/product/${banner.linkTarget}`);
    } else if (banner.linkType === 'collection' && banner.linkTarget) {
      // Navigate to collection page
      router.push(`/collections/${banner.linkTarget}`);
    } else if (banner.linkType === 'brand' && banner.linkTarget) {
      // Navigate to search page filtered by brand
      router.push(`/search?brand=${encodeURIComponent(banner.linkTarget)}`);
    }
  };

  const handleCarouselNext = (bannerId: string, totalImages: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCarouselIndices(prev => ({
      ...prev,
      [bannerId]: ((prev[bannerId] || 0) + 1) % totalImages
    }));
  };

  const handleCarouselPrev = (bannerId: string, totalImages: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCarouselIndices(prev => ({
      ...prev,
      [bannerId]: ((prev[bannerId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const getBannerImage = (banner: SponsoredBanner): string | null => {
    // Check if banner has carousel images
    if (banner.bannerImages && banner.bannerImages.length > 0) {
      const currentIndex = carouselIndices[banner.id] || 0;
      const currentImage = banner.bannerImages[currentIndex];
      if (currentImage) {
        // For mobile, prefer mobileUri but fallback to desktopUri if not available
        if (isMobile) {
          return currentImage.mobileUri || currentImage.desktopUri || null;
        }
        // For desktop, use desktopUri
        return currentImage.desktopUri || null;
      }
    }
    
    // Fallback to single desktop/mobile images
    if (isMobile) {
      // On mobile, prefer mobile image but fallback to desktop if mobile not available
      return banner.mobileImageUri || banner.desktopImageUri || banner.bannerImageUri;
    }
    // On desktop, use desktop image
    return banner.desktopImageUri || banner.bannerImageUri;
  };

  const hasCarousel = (banner: SponsoredBanner): boolean => {
    return !!(banner.bannerImages && banner.bannerImages.length > 1);
  };

  // Inline aspect ratio style to ensure exact height based on provided ratios
  const getAspectStyle = (
    desktopRatio?: string,
    mobileRatio?: string
  ): React.CSSProperties => {
    const ratio = isMobile ? mobileRatio : desktopRatio;
    if (!ratio) return {};
    // CSS aspect-ratio expects "width / height"
    const value = ratio.replace('/', ' / ');
    return { aspectRatio: value };
  };

  // Banners without an uploaded image yet render a "1360x250"-style mockup
  // box (solid background + centered dimensions) instead of nothing.
  const getPlaceholderLabel = (desktopRatio?: string, mobileRatio?: string): string => {
    const ratio = (isMobile ? mobileRatio : desktopRatio) || (isMobile ? '328/210' : '1360/250');
    return ratio.replace('/', 'x');
  };

  return (
    <div className={`sponsored-banners ${className}`}>
      <div className="flex flex-col gap-4">
        {banners.map((banner, index) => {
          const imageUrl = getBannerImage(banner);
          const isCarousel = hasCarousel(banner);
          const carouselImages = banner.bannerImages || [];
          const currentCarouselIndex = carouselIndices[banner.id] || 0;
          const aspectStyle = getAspectStyle(banner.desktopRatio, banner.mobileRatio);
          
          return (
            <div key={banner.id} className="w-full">
              <div
                onClick={(e) => handleBannerClick(banner, e)}
                className="relative cursor-pointer w-full overflow-hidden rounded-xl"
                style={aspectStyle}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={banner.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '20px'
                    }}
                  />
                ) : (
                  <div
                    className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-400"
                    style={{ borderRadius: '20px' }}
                  >
                    <span className="text-white font-bold text-center px-4" style={{ fontSize: 'clamp(1rem, 4vw, 2rem)' }}>
                      {getPlaceholderLabel(banner.desktopRatio, banner.mobileRatio)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Carousel Navigation Controls - Below Banner */}
              {isCarousel && carouselImages.length > 1 && (
                <div className="flex items-center justify-center -mt-3 z-10 relative">
                  <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-1 py-0.5">
                    {/* Previous Button */}
                    <Button
                      type="button"
                      variant="icon"
                      color="white"
                      size="icon-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCarouselPrev(banner.id, carouselImages.length, e);
                      }}
                      className="carousel-control h-6 w-6 rounded-full bg-constant-white text-gray-600 hover:bg-gray-50"
                      aria-label="Previous image"
                    >
                      <ChevronLeftIcon size={10} className="w-2.5 h-2.5" />
                    </Button>
                    
                    {/* Carousel Dots */}
                    <div className="flex items-center gap-1 px-1.5">
                      {carouselImages.map((_, imgIndex) => (
                        <Button
                          key={imgIndex}
                          type="button"
                          variant="icon"
                          color="white"
                          size="icon-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCarouselIndices(prev => ({ ...prev, [banner.id]: imgIndex }));
                          }}
                          className={`w-1.5 h-1.5 rounded-full transition-colors ${
                            currentCarouselIndex === imgIndex 
                              ? 'bg-blue-600' 
                              : 'bg-gray-300'
                          }`}
                          aria-label={`Go to image ${imgIndex + 1}`}
                        />
                      ))}
                    </div>
                    
                    {/* Next Button */}
                    <Button
                      type="button"
                      variant="icon"
                      color="white"
                      size="icon-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCarouselNext(banner.id, carouselImages.length, e);
                      }}
                      className="carousel-control h-6 w-6 rounded-full bg-constant-white text-gray-600 hover:bg-gray-50"
                      aria-label="Next image"
                    >
                      <ChevronRightIcon size={10} className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
